import { useParams, Link, useNavigate, Navigate } from 'react-router-dom';
import { useDashboard } from '../../hooks/useDashboard';
import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useDebounce } from 'use-debounce';
import ReactGridLayout, { useContainerWidth } from 'react-grid-layout';
import type { Layout, LayoutItem } from 'react-grid-layout';
import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '../../components/ui/breadcrumb';
import { Loader2, RefreshCw, RotateCcw, Settings, Plus, Save, GripVertical, Trash2, AlertCircle, ChevronDown } from 'lucide-react';
import { Chart } from '../../components/Chart';
import { EditableTitle } from '../../components/EditableTitle';
import type { DashboardComponent } from '../../services/dashboard.service';
import { dashboardService } from '../../services/dashboard.service';
import { MarkdownText } from '../../components/chat/MarkdownText';
import { Button } from '../../components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../components/ui/dropdown-menu';
import { Input } from '../../components/ui/input';
import { useQueryClient, useQuery } from '@tanstack/react-query';
import { analysisService, type Analysis } from '../../services/analysis.service';
import { useAnalysis } from '../../hooks/useAnalysis';
import { toastService } from '../../services/toast.service';
import { Combobox } from '../../components/ui/combobox';
import { MultiSelectCombobox } from '../../components/ui/multi-select-combobox';
import { useUniqueValuesInDimension } from '../../services/reportBuilder.service';
import type { AnalysisDimensionFilter } from '../../utils/filterComparators';
import { useIsInDraftMode } from '../../hooks/useDraftMode';
import { useDimensionById } from '../../hooks/useDimension';
import { useIsMobile } from '../../hooks/use-mobile';
import { SidebarTrigger } from '../../components/ui/sidebar';
import { useWhitelabelValue } from '../../hooks/useWhitelabelOverrides';
import { ComponentConfigSheet } from '../../components/ComponentConfigSheet';
import { useImmer } from 'use-immer';
import { resolveComponentTitle, resolveComponentDescription } from '../../utils/dashboardComponentResolvers';
import { useUnsavedChangesGuard } from '../../hooks/useUnsavedChangesGuard';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';

import { DateRangePicker, DATE_RANGE_OPTIONS, mapDateRangeFromBackend, mapDateRangeToBackend } from '../../components/ui/date-range-picker';
import { useAvailableDashboardDateRangeOptions } from '../../hooks/useAvailableDashboardDateRangeOptions';
import { Checkbox } from '../../components/ui/checkbox';
import { Label } from '../../components/ui/label';
import { dateDimensionCustomPeriodService } from '../../services/dateDimensionCustomPeriod.service';
import { useFeatureFlag, useFeatureFlagResolved } from '../../services/featureFlag.service';
import { FeatureFlag } from '../../services/featureFlag.service';
import { useTenantConfig } from '../../hooks/useTenantConfig';
import {
  buildPersistenceKey,
  diffFromDefaults,
  mergePersistedOverDefaults,
  readPersistedFromStorage,
  reconcilePersistedFilters,
  writePersistedToStorage,
  type DashboardDefaults,
} from '../../utils/dashboardFilterPersistence';

import { simplePlural } from '../../utils/pluralize';

interface DashboardComponentRendererProps {
  component: DashboardComponent;
  dateRangeConfig?: any;
  dimensionFilters?: AnalysisDimensionFilter[];
  pendingDimensionFilters?: AnalysisDimensionFilter[];
  forceShowPrepLoader?: boolean;
  onFilterChange?: (componentId: string, filter: AnalysisDimensionFilter | null) => void;
  currentFilter?: AnalysisDimensionFilter | null;
  disabled?: boolean;
  onApplyFilters?: () => void;
  filterApplyDisabled?: boolean;
  onChartRefresh?: () => void;
  onChartRefreshFinished?: () => void;
  chartRefreshDisabled?: boolean;
  isEditing?: boolean;
  onConfigure?: (component: DashboardComponent) => void;
  componentError?: string;
}

interface DefaultValueChange {
  id: string;
  type: 'filter_value' | 'date_range';
  label: string;
  description: string;
}

interface FilterComponentProps {
  filterConfig: any; // DashboardFilterOption
  componentId: string;
  onFilterChange?: (componentId: string, filter: AnalysisDimensionFilter | null) => void;
  currentFilter?: AnalysisDimensionFilter | null;
  activeFilter?: AnalysisDimensionFilter | null;
  otherActiveFilters?: AnalysisDimensionFilter[];
  disabled?: boolean;
  onApply?: () => void;
  applyDisabled?: boolean;
}

function FilterComponent({
  filterConfig,
  componentId,
  onFilterChange,
  currentFilter,
  activeFilter,
  otherActiveFilters,
  disabled = false,
  onApply,
  applyDisabled = false,
}: FilterComponentProps) {
  const [query, setQuery] = useState<string>('');
  const [debouncedQuery] = useDebounce(query, 500);

  // Only support DIMENSION type filters
  if (filterConfig?.filter_type !== 'DIMENSION' || !filterConfig?.dimension_id) {
    return (
      <div className="p-4 h-full flex items-center justify-center text-muted-foreground">
        <div className="text-center">
          <p className="text-sm">Unsupported filter type</p>
          <p className="text-xs mt-2">Only DIMENSION filters are supported</p>
        </div>
      </div>
    );
  }

  const { data: dimensionValues, isLoading } = useUniqueValuesInDimension(
    filterConfig.dimension_id,
    debouncedQuery,
    otherActiveFilters && otherActiveFilters.length > 0 ? otherActiveFilters : undefined,
  );

  const { data: dimension } = useDimensionById(filterConfig.dimension_id);

  const getFilterValues = useCallback((filter?: AnalysisDimensionFilter | null) => {
    if (filter?.value) {
      if (Array.isArray(filter.value)) {
        return filter.value;
      }
      return [filter.value];
    }
    return [];
  }, []);

  const selectedValues = useMemo(() => getFilterValues(currentFilter), [currentFilter, getFilterValues]);

  const valueOptions = useMemo(() => {
    const dbValues = dimensionValues ?? [];
    const dbSet = new Set(dbValues);
    // Always include currently selected values so the user can see and clear them,
    // even if they've been filtered out by other active filters
    const extra = selectedValues.filter(v => !dbSet.has(v));
    const allValues = [...dbValues, ...extra];
    return allValues.sort((a, b) => a.localeCompare(b)).map(v => ({
      value: v,
      label: v.length > 50 ? `${v.slice(0, 50)}...` : v,
    }));
  }, [dimensionValues, selectedValues]);

  const allowMultiple = filterConfig.allow_multiple ?? false;

  const placeholder = useMemo(() => {
    if (dimensionValues?.length === 1) return dimensionValues[0];
    if (dimension?.name) return `All ${simplePlural(dimension.name)}`;
    return 'All values';
  }, [dimensionValues, dimension?.name]);

  const currentValues = useMemo(() => getFilterValues(currentFilter), [currentFilter, getFilterValues]);
  const activeValues = useMemo(() => getFilterValues(activeFilter), [activeFilter, getFilterValues]);
  const hasPendingChanges = useMemo(
    () => JSON.stringify(currentValues) !== JSON.stringify(activeValues),
    [currentValues, activeValues]
  );

  const defaultValues = useMemo(() => {
    const dv = filterConfig.default_value;
    if (dv == null) return [] as string[];
    return Array.isArray(dv) ? dv : [dv];
  }, [filterConfig.default_value]);

  const differsFromDefault = useMemo(() => {
    const cur = [...currentValues].sort();
    const def = [...defaultValues].sort();
    return JSON.stringify(cur) !== JSON.stringify(def);
  }, [currentValues, defaultValues]);

  // Show the reset link when there's somewhere meaningful to reset *to*:
  // - a saved default exists, OR
  // - the user has previously applied a value (activeValues non-empty ⇒
  //   it's in the per-tab session cache). The "Clear selection" variant
  //   only ever appears when the user has actually committed a value,
  //   not while they're mid-typing a fresh selection.
  const showResetLink = useMemo(
    () => differsFromDefault && (defaultValues.length > 0 || activeValues.length > 0),
    [differsFromDefault, defaultValues, activeValues],
  );

  const handleResetToDefault = () => {
    if (defaultValues.length === 0) {
      onFilterChange?.(componentId, null);
      return;
    }
    onFilterChange?.(componentId, {
      dimension_id: filterConfig.dimension_id,
      comparator: 'IN',
      value: defaultValues,
    });
  };

  const handleValueChange = (values: string | string[]) => {
    // Normalize: single-select Combobox clears via onValueChange(''); treat the
    // empty string as "cleared" rather than as a real value.
    const filterValue = Array.isArray(values)
      ? values.filter((v) => v !== '')
      : values
        ? [values]
        : [];

    if (filterValue.length === 0) {
      onFilterChange?.(componentId, null);
      return;
    }

    const filter: AnalysisDimensionFilter = {
      dimension_id: filterConfig.dimension_id,
      comparator: 'IN',
      value: filterValue,
    };

    onFilterChange?.(componentId, filter);
  };

  return (
    <div className="px-4 h-full flex flex-col justify-center">
      {filterConfig.description && (
        <p className="text-sm text-muted-foreground mb-2">{filterConfig.description}</p>
      )}
      {filterConfig.input_label && (
        <label className="text-xs font-medium text-muted-foreground mb-1.5">
          {filterConfig.input_label}
        </label>
      )}
      <div className="flex items-center gap-2">
        <div className="flex-1 min-w-0">
          {allowMultiple ? (
            <MultiSelectCombobox
              options={valueOptions}
              value={currentValues}
              onValueChange={(values) => handleValueChange(values)}
              placeholder={placeholder}
              emptyText="No values available."
              onInputChange={setQuery}
              isLoading={isLoading}
              disabled={disabled}
              className="w-full"
            />
          ) : (
            <Combobox
              options={valueOptions}
              value={currentValues.length > 0 ? currentValues[0] : undefined}
              onValueChange={(value) => handleValueChange(value ?? '')}
              placeholder={placeholder}
              emptyText="No values available."
              disabled={disabled}
              className="w-full"
            />
          )}
        </div>
        <Button
          size="sm"
          onClick={() => onApply?.()}
          disabled={disabled || applyDisabled || !hasPendingChanges}
        >
          Apply
        </Button>
      </div>
      <button
        type="button"
        onClick={handleResetToDefault}
        disabled={disabled || !showResetLink}
        aria-hidden={!showResetLink}
        tabIndex={showResetLink ? 0 : -1}
        title={
          defaultValues.length > 0
            ? `Reset to default: ${defaultValues.join(', ')}`
            : 'Clear selection'
        }
        className={`mt-2 self-start inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground disabled:cursor-not-allowed max-w-full transition-opacity ${
          showResetLink ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
      >
        <RotateCcw className="h-3 w-3 flex-shrink-0" />
        <span className="truncate">
          {defaultValues.length === 0
            ? 'Clear selection'
            : defaultValues.length === 1
              ? `Reset to default: ${defaultValues[0]}`
              : `Reset to default (${defaultValues.length} values)`}
        </span>
      </button>
    </div>
  );
}

function DashboardComponentRenderer({
  component,
  dateRangeConfig,
  dimensionFilters,
  pendingDimensionFilters,
  forceShowPrepLoader = false,
  onFilterChange,
  currentFilter,
  disabled = false,
  onApplyFilters,
  filterApplyDisabled = false,
  onChartRefresh,
  onChartRefreshFinished,
  chartRefreshDisabled = false,
  isEditing = false,
  onConfigure,
  componentError,
}: DashboardComponentRendererProps) {
  const [isHovered, setIsHovered] = useState(false);

  // Fetch linked object data for title/description fallback (cached, no extra API calls)
  const { data: linkedAnalysis, isLoading: analysisLoading } = useAnalysis(
    component.object_type === 'ANALYSIS' ? component.object_id || null : null,
    true,
  );
  const { data: linkedDimension, isLoading: dimensionLoading } = useDimensionById(
    component.object_type === 'FILTER' ? component.filter_config?.dimension_id || '' : '',
    { enabled: component.object_type === 'FILTER' && !!component.filter_config?.dimension_id },
  );

  // Resolve title/description: component override → linked object → fallback
  const linkedObject = component.object_type === 'ANALYSIS' ? linkedAnalysis : linkedDimension;
  const linkedObjectLoading = component.object_type === 'ANALYSIS' ? analysisLoading : dimensionLoading;
  const resolvedTitle = useMemo(() => {
    if (component.title) return component.title;
    if (linkedObjectLoading) return '';
    return resolveComponentTitle(component, linkedObject);
  }, [component.title, component.object_type, linkedObject?.name, linkedObjectLoading]);
  const resolvedDescription = useMemo(
    () => resolveComponentDescription(component, linkedObject),
    [component.description, component.object_type, linkedObject?.description],
  );

  const renderContent = () => {
    if (componentError) {
      return (
        <div className="p-4 h-full flex items-center justify-center">
          <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md w-full">
            <div className="flex items-start gap-2">
              <AlertCircle className="h-4 w-4 text-destructive mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-destructive text-sm font-medium">Build failed</p>
                <p className="text-destructive/80 text-xs mt-1">{componentError}</p>
              </div>
            </div>
          </div>
        </div>
      );
    }

    if (component.object_type === 'ANALYSIS' && component.object_id) {
      // Map date range from backend format to frontend format
      const mappedDateRange = component.ignore_date_range 
        ? undefined 
        : mapDateRangeFromBackend(dateRangeConfig?.date_range);
      
      // Render chart component with auto-prep disabled (dashboard handles prep centrally)
      return (
        <Chart
          analysisId={component.object_id}
          componentId={component.id}
          dateRange={mappedDateRange}
          customDateStart={component.ignore_date_range ? undefined : dateRangeConfig?.custom_date_range_start}
          customDateEnd={component.ignore_date_range ? undefined : dateRangeConfig?.custom_date_range_end}
          dimensionFilters={dimensionFilters}
          disableAutoPrep={true}
          compact={true}
          forceShowPrepLoader={forceShowPrepLoader}
          onRefresh={onChartRefresh}
          onRefreshFinished={onChartRefreshFinished}
          refreshDisabled={chartRefreshDisabled}
          hideRefreshButton={true}
          disableSave={true}
          disableAddToDashboard={true}
          enableExploreFromHere={true}
          hideToolbar={component.hide_toolbar}
          disableSort={component.disable_sort}
        />
      );
    } else if (component.object_type === 'MARKDOWN' && component.markdown_content) {
      // Placeholder for markdown - will implement later
      return (
        <div className="p-4 h-full overflow-auto">
          <MarkdownText content={component.markdown_content} />
        </div>
      );
    } else if (component.object_type === 'FILTER' && component.filter_config) {
      return (
        <FilterComponent
          filterConfig={component.filter_config}
          componentId={component.id}
          onFilterChange={onFilterChange}
          currentFilter={currentFilter}
          activeFilter={dimensionFilters?.find((filter) => filter.dimension_id === component.filter_config?.dimension_id) || null}
          otherActiveFilters={pendingDimensionFilters?.filter((filter) => filter.dimension_id !== component.filter_config?.dimension_id)}
          disabled={disabled}
          onApply={onApplyFilters}
          applyDisabled={filterApplyDisabled}
        />
      );
    } else {
      // Placeholder for other types
      return (
        <div className="p-4 h-full flex items-center justify-center text-muted-foreground">
          <div className="text-center">
            <p className="text-sm">Component type: {component.object_type || 'Unknown'}</p>
            <p className="text-xs mt-2">Implementation coming soon</p>
          </div>
        </div>
      );
    }
  };

  const showHeader = !component.hide_header;

  // Card header bg: "transparent" = no bg/border, hex = custom, "" = default theme
  const cardHeaderBg = useWhitelabelValue('card_header_bg_color', '');
  const cardTitleColor = useWhitelabelValue('card_title_color', '');
  const isTransparentHeader = cardHeaderBg === 'transparent';
  const headerStyle = cardHeaderBg && !isTransparentHeader ? { backgroundColor: cardHeaderBg + '80' } : undefined;
  const headerClassName = isTransparentHeader
    ? 'px-4 py-2 flex-shrink-0 flex items-center justify-between'
    : `px-4 py-2 border-b border-border flex-shrink-0 flex items-center justify-between ${headerStyle ? '' : 'bg-muted/50'}`;

  return (
    <div
      className="w-full h-full flex flex-col bg-background border border-border rounded-md overflow-hidden relative"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Component Header - only shown if hide_header is false */}
      {showHeader && (
        <div className={headerClassName} style={headerStyle}>
          <div className="min-w-0 flex-1">
            <h3 className={`text-sm font-semibold truncate ${cardTitleColor ? '' : 'text-foreground'}`} style={cardTitleColor ? { color: cardTitleColor } : undefined}>{resolvedTitle}</h3>
            {resolvedDescription && (
              <p className="text-xs text-muted-foreground break-words" title={resolvedDescription}>{resolvedDescription}</p>
            )}
          </div>
          {isEditing && (
            <div className="flex items-center gap-1">
              <div className="drag cursor-move p-1 hover:bg-muted rounded">
                <GripVertical className="h-4 w-4 text-muted-foreground" />
              </div>
              {onConfigure && (
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 nodrag"
                  onClick={() => onConfigure(component)}
                >
                  <Settings className="h-4 w-4" />
                </Button>
              )}
            </div>
          )}
        </div>
      )}

      {/* Floating controls when header is hidden - only visible on hover */}
      {!showHeader && isEditing && isHovered && (
        <div className="absolute top-2 right-2 flex items-center gap-1 z-10 bg-background/80 backdrop-blur-sm rounded border border-border p-1">
          <div className="drag cursor-move p-1 hover:bg-muted rounded">
            <GripVertical className="h-4 w-4 text-muted-foreground" />
          </div>
          {onConfigure && (
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6 nodrag"
              onClick={() => onConfigure(component)}
            >
              <Settings className="h-4 w-4" />
            </Button>
          )}
        </div>
      )}

      {/* Component Content */}
      <div className="flex-1 min-h-0 overflow-hidden nodrag">
        {renderContent()}
      </div>
    </div>
  );
}

interface DashboardDetailProps {
  embeddedDashboardId?: string;
}

// Outer gate — calls only the flag hook. Once the flag resolves, mounts
// DashboardDetailContent (which has the rest of the hooks). Splitting like
// this keeps hook order stable across renders: while loading, only the flag
// hook runs; after loading, the inner component is mounted with its own
// hook list.
export default function DashboardDetail({ embeddedDashboardId }: DashboardDetailProps = {}) {
  const { value: isAnalysisSectionEnabled, isLoading: isFlagLoading } =
    useFeatureFlagResolved(FeatureFlag.ACCESS_ANALYSIS_SECTION, false);

  // When accessed via route (not embedded), gate on the feature flag.
  // Wait for the flag to resolve before redirecting so a cold direct-URL
  // load doesn't bounce users home before LaunchDarkly has reported in.
  if (!embeddedDashboardId) {
    if (isFlagLoading) {
      return (
        <div className="flex items-center justify-center h-screen">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      );
    }
    if (!isAnalysisSectionEnabled) {
      return <Navigate to="/" replace />;
    }
  }

  return <DashboardDetailContent embeddedDashboardId={embeddedDashboardId} />;
}

function DashboardDetailContent({ embeddedDashboardId }: DashboardDetailProps = {}) {
  const { dashboardId: routeDashboardId } = useParams<{ dashboardId: string }>();
  const dashboardId = embeddedDashboardId || routeDashboardId;
  const { data: dashboard, isLoading, error } = useDashboard(dashboardId || null);
  const { data: tenantConfig } = useTenantConfig();
  const supersetsEnabled = !!tenantConfig?.dashboard_supersets_enabled;
  const queryClient = useQueryClient();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isChartRefreshing, setIsChartRefreshing] = useState(false); // Track if any chart is refreshing
  const [componentErrors, setComponentErrors] = useState<Map<string, string>>(new Map());
  const { width: containerWidth, containerRef, mounted: containerMounted, measureWidth } = useContainerWidth();
  const isInDraftMode = useIsInDraftMode();
  const isMobile = useIsMobile();
  const navigate = useNavigate();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const { data: customPeriodVariants } = useQuery({
    queryKey: ['customPeriodVariants'],
    queryFn: () => dateDimensionCustomPeriodService.getVariants(),
    staleTime: 5 * 60 * 1000,
  });
  const hasFiscalPeriods = (customPeriodVariants?.length ?? 0) > 0;
  const availableDateRangeOptions = useAvailableDashboardDateRangeOptions(hasFiscalPeriods);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDeleteDashboard = useCallback(async () => {
    if (!dashboardId) return;
    setIsDeleting(true);
    try {
      await dashboardService.delete(dashboardId);
      await queryClient.invalidateQueries({ queryKey: ['dashboards'] });
      toastService.success('Dashboard deleted successfully');
      navigate('/analytics');
    } finally {
      setIsDeleting(false);
      setShowDeleteDialog(false);
    }
  }, [dashboardId, queryClient, navigate]);

  // Debug logging for container width issues
  useEffect(() => {
    console.log('[DashboardDetail] containerWidth:', containerWidth, 'containerMounted:', containerMounted);
  }, [containerWidth, containerMounted]);

  // Force measure width when dashboard loads (the ref element wasn't available during initial mount)
  useEffect(() => {
    if (dashboard && !isLoading) {
      console.log('[DashboardDetail] Dashboard loaded, calling measureWidth');
      // Use requestAnimationFrame to ensure DOM has updated
      requestAnimationFrame(() => {
        measureWidth();
      });
    }
  }, [dashboard, isLoading, measureWidth]);

  // Editing state
  const [editingComponents, setEditingComponents] = useImmer<DashboardComponent[]>([]);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Config sheet state
  const [configSheetOpen, setConfigSheetOpen] = useState(false);
  const [configuringComponent, setConfiguringComponent] = useState<DashboardComponent | null>(null);

  // Save confirmation dialog state
  const [showSaveConfirmDialog, setShowSaveConfirmDialog] = useState(false);
  const [saveChangeChecked, setSaveChangeChecked] = useState<Map<string, boolean>>(new Map());

  // Initialize editing components when dashboard loads or draft mode changes
  useEffect(() => {
    if (dashboard?.components && isInDraftMode) {
      setEditingComponents(dashboard.components);
      setHasUnsavedChanges(false);
    }
  }, [dashboard?.id, isInDraftMode]);

  // Get the components to display (editing or original)
  const displayComponents = isInDraftMode ? editingComponents : (dashboard?.components || []);

  const mobileComponents = useMemo(() => {
    return [...displayComponents].sort((a, b) => a.y - b.y || a.x - b.x);
  }, [displayComponents]);
  
  // Pending date range state (what user is selecting, not yet applied)
  const [pendingDateRange, setPendingDateRange] = useState<string>('all_time');
  const [pendingCustomDateStart, setPendingCustomDateStart] = useState<string>('');
  const [pendingCustomDateEnd, setPendingCustomDateEnd] = useState<string>('');

  
  // Active date range config (what's currently passed to components)
  const [activeDateRangeConfig, setActiveDateRangeConfig] = useState<any>(undefined);
  
  // Active dimension filters (what's currently passed to components)
  const [dimensionFilters, setDimensionFilters] = useState<AnalysisDimensionFilter[]>([]);
  
  // Pending dimension filters (what user is selecting, not yet applied)
  const [pendingDimensionFilters, setPendingDimensionFilters] = useState<AnalysisDimensionFilter[]>([]);
  
  // Session-storage key for filter persistence (null in embed mode or before tenant loads)
  const filterStorageKey = useMemo(
    () =>
      embeddedDashboardId
        ? null
        : buildPersistenceKey(tenantConfig?.tenant_uuid, dashboardId || undefined),
    [embeddedDashboardId, tenantConfig?.tenant_uuid, dashboardId],
  );

  // Dashboard's saved defaults — used both as the merge base for persistence and
  // as the comparison source for the "Reset" affordance.
  const dashboardDefaults = useMemo<DashboardDefaults>(() => {
    const drc = dashboard?.date_range_config;
    const mappedRange = drc ? mapDateRangeFromBackend(drc.date_range) : null;
    const dimensionFilters: AnalysisDimensionFilter[] = [];
    (dashboard?.components || []).forEach((component) => {
      if (component.object_type === 'FILTER' && component.filter_config) {
        const fc = component.filter_config;
        if (fc.filter_type === 'DIMENSION' && fc.dimension_id && fc.default_value) {
          const values = Array.isArray(fc.default_value)
            ? fc.default_value
            : [fc.default_value];
          if (values.length > 0) {
            dimensionFilters.push({
              dimension_id: fc.dimension_id,
              comparator: 'IN',
              value: values,
            });
          }
        }
      }
    });
    return {
      dateRange: mappedRange || 'all_time',
      customStart: drc?.custom_date_range_start || '',
      customEnd: drc?.custom_date_range_end || '',
      dimensionFilters,
    };
  }, [dashboard?.id]);

  const buildDateRangeConfig = useCallback(
    (dr: { dateRange: string; customStart: string; customEnd: string }) => ({
      date_range: mapDateRangeToBackend(dr.dateRange),
      custom_date_range_start: dr.dateRange === 'custom' ? dr.customStart : undefined,
      custom_date_range_end: dr.dateRange === 'custom' ? dr.customEnd : undefined,
      user_timezone: dashboard?.date_range_config?.user_timezone || 'UTC',
      previous_period: dashboard?.date_range_config?.previous_period || false,
    }),
    [dashboard?.date_range_config?.user_timezone, dashboard?.date_range_config?.previous_period],
  );

  const applyMergedFilters = useCallback(
    (merged: DashboardDefaults) => {
      setPendingDateRange(merged.dateRange);
      setPendingCustomDateStart(merged.customStart);
      setPendingCustomDateEnd(merged.customEnd);
      setPendingDimensionFilters(merged.dimensionFilters);
      setDimensionFilters(merged.dimensionFilters);
      setActiveDateRangeConfig(buildDateRangeConfig(merged));
    },
    [buildDateRangeConfig],
  );

  // Track which dashboard has been initialized so persisted writes don't re-trigger init
  const initializedDashboardIdRef = useRef<string | null>(null);

  // Single init effect: merge persisted overrides over dashboard defaults, then seed
  // BOTH pending and active state so charts fire one query, not two.
  useEffect(() => {
    if (!dashboard) return;
    // If persistence is enabled, wait for the storage key to resolve (tenantConfig load).
    if (!embeddedDashboardId && !filterStorageKey) return;
    if (initializedDashboardIdRef.current === dashboard.id) return;

    const persisted = readPersistedFromStorage(filterStorageKey);
    const reconciled = reconcilePersistedFilters(
      persisted,
      dashboard,
      availableDateRangeOptions.map((o) => o.value),
    );
    const merged = mergePersistedOverDefaults(reconciled, dashboardDefaults);
    applyMergedFilters(merged);

    // If reconcile pruned anything, write the cleaned blob back.
    if (filterStorageKey && JSON.stringify(reconciled) !== JSON.stringify(persisted)) {
      writePersistedToStorage(filterStorageKey, reconciled);
    }
    initializedDashboardIdRef.current = dashboard.id;
  }, [
    dashboard,
    embeddedDashboardId,
    filterStorageKey,
    dashboardDefaults,
    availableDateRangeOptions,
    applyMergedFilters,
  ]);

  // Persist the just-applied filter state as a diff vs dashboard defaults.
  // Called from handleRefresh at the same moment we promote pending → active,
  // so unapplied pending changes don't leak into sessionStorage.
  const persistAppliedFilters = useCallback(
    (applied: {
      dateRange: string;
      customStart: string;
      customEnd: string;
      dimensionFilters: AnalysisDimensionFilter[];
    }) => {
      if (!filterStorageKey) return;
      const diff = diffFromDefaults(applied, dashboardDefaults);
      writePersistedToStorage(filterStorageKey, diff);
    },
    [filterStorageKey, dashboardDefaults],
  );

  const dateRangeDiffersFromDefault = useMemo(() => {
    if (!dashboard) return false;
    return (
      pendingDateRange !== dashboardDefaults.dateRange ||
      (pendingDateRange === 'custom' &&
        (pendingCustomDateStart !== dashboardDefaults.customStart ||
          pendingCustomDateEnd !== dashboardDefaults.customEnd))
    );
  }, [
    dashboard,
    pendingDateRange,
    pendingCustomDateStart,
    pendingCustomDateEnd,
    dashboardDefaults,
  ]);

  const resetDateRangeToDefault = useCallback(() => {
    setPendingDateRange(dashboardDefaults.dateRange);
    setPendingCustomDateStart(dashboardDefaults.customStart);
    setPendingCustomDateEnd(dashboardDefaults.customEnd);
  }, [dashboardDefaults]);

  // Build pending date range config (used for refresh)
  const pendingDateRangeConfig = useMemo(() => {
    if (!pendingDateRange) return undefined;
    
    return {
      date_range: mapDateRangeToBackend(pendingDateRange),
      custom_date_range_start: pendingDateRange === 'custom' ? pendingCustomDateStart : undefined,
      custom_date_range_end: pendingDateRange === 'custom' ? pendingCustomDateEnd : undefined,
      user_timezone: dashboard?.date_range_config?.user_timezone || 'UTC',
      previous_period: dashboard?.date_range_config?.previous_period || false,
    };
  }, [pendingDateRange, pendingCustomDateStart, pendingCustomDateEnd, dashboard?.date_range_config?.user_timezone, dashboard?.date_range_config?.previous_period]);

  // Compute filter and date range changes from saved defaults
  const defaultValueChanges = useMemo((): DefaultValueChange[] => {
    if (!dashboard) return [];
    const changes: DefaultValueChange[] = [];

    // Check date range changes
    const savedConfig = dashboard.date_range_config;
    const savedDR = savedConfig?.date_range || 'ALL_TIME';
    const pendingDR = mapDateRangeToBackend(pendingDateRange) || 'ALL_TIME';

    const dateRangeChanged = savedDR !== pendingDR ||
      (pendingDR === 'CUSTOM' && (
        (savedConfig?.custom_date_range_start || '') !== pendingCustomDateStart ||
        (savedConfig?.custom_date_range_end || '') !== pendingCustomDateEnd
      ));

    if (dateRangeChanged) {
      const savedLabel = DATE_RANGE_OPTIONS.find(o => o.value === mapDateRangeFromBackend(savedDR))?.label || savedDR;
      let pendingLabel = DATE_RANGE_OPTIONS.find(o => o.value === pendingDateRange)?.label || pendingDateRange;
      if (pendingDateRange === 'custom') {
        pendingLabel = `Custom (${pendingCustomDateStart} to ${pendingCustomDateEnd})`;
      }
      changes.push({
        id: 'date_range',
        type: 'date_range',
        label: 'Date Range',
        description: `${savedLabel} \u2192 ${pendingLabel}`,
      });
    }

    // Check filter value changes
    const componentsToCheck = isInDraftMode ? editingComponents : (dashboard.components || []);
    componentsToCheck.forEach(component => {
      if (component.object_type !== 'FILTER' || !component.filter_config?.dimension_id) return;

      const fc = component.filter_config;
      const dimId = fc.dimension_id!;
      const savedDefault = fc.default_value;
      const savedValues = savedDefault ? (Array.isArray(savedDefault) ? savedDefault : [savedDefault]) : [];

      const pendingFilter = pendingDimensionFilters.find(f => f.dimension_id === dimId);
      const pendingValues = pendingFilter?.value
        ? (Array.isArray(pendingFilter.value) ? pendingFilter.value : [pendingFilter.value])
        : [];

      if (JSON.stringify([...savedValues].sort()) !== JSON.stringify([...pendingValues].sort())) {
        const label = fc.input_label || component.title || dimId;
        const savedStr = savedValues.length > 0 ? savedValues.join(', ') : '(none)';
        const pendingStr = pendingValues.length > 0 ? pendingValues.join(', ') : '(none)';
        changes.push({
          id: `filter_${dimId}`,
          type: 'filter_value',
          label: `Filter: ${label}`,
          description: `${savedStr} \u2192 ${pendingStr}`,
        });
      }
    });

    return changes;
  }, [dashboard, pendingDateRange, pendingCustomDateStart, pendingCustomDateEnd, pendingDimensionFilters, editingComponents, isInDraftMode]);

  const hasDefaultValueChanges = defaultValueChanges.length > 0;

  // Poll orchestration status until it reaches a terminal status, return final orchestration
  // Terminal statuses: COMPLETE, ERROR, IN_REVIEW, REJECTED, CANCELLED
  // Non-terminal statuses: QUEUED, RUNNING, NOT_BUILDING, etc.
  const pollOrchestrationStatus = async (orchestrationId: string): Promise<any> => {
    const terminalStatuses = ['COMPLETE', 'ERROR', 'IN_REVIEW', 'REJECTED', 'CANCELLED'];
    let status = 'QUEUED';
    let iteration = 0;
    const maxIterations = 120; // 4 minutes max (120 * 2 seconds)

    while (!terminalStatuses.includes(status) && iteration < maxIterations) {
      await new Promise((resolve) => setTimeout(resolve, 2000));
      
      try {
        const orchestration = await analysisService.getOrchestrationStatus(orchestrationId);
        status = orchestration.status;

        // If we've reached a terminal status, return the final orchestration
        if (terminalStatuses.includes(status)) {
          // Re-fetch orchestration to get final state with all build_executions
          const finalOrchestration = await analysisService.getOrchestrationStatus(orchestrationId);
          return finalOrchestration;
        }
      } catch (err) {
        if (iteration < maxIterations - 1 && err instanceof Error && !err.message.includes('Build failed')) {
          // Continue polling on transient errors (but not build failures)
          continue;
        }
        throw err;
      }
      iteration++;
    }

    if (iteration >= maxIterations) {
      throw new Error('Max wait time exceeded');
    }
    
    // Should not reach here, but return the last orchestration if we do
    return await analysisService.getOrchestrationStatus(orchestrationId);
  };

  // Handle date range change (updates pending state only, not applied until refresh)
  const handleDateRangeChange = (newDateRange: string, customStart?: string, customEnd?: string) => {
    setPendingDateRange(newDateRange);
    if (newDateRange === 'custom') {
      setPendingCustomDateStart(customStart || '');
      setPendingCustomDateEnd(customEnd || '');
    } else {
      setPendingCustomDateStart('');
      setPendingCustomDateEnd('');
    }
  };

  // Handle filter change from filter components (updates pending state only, not applied until refresh)
  const handleFilterChange = useCallback((componentId: string, filter: AnalysisDimensionFilter | null) => {
    const componentsToSearch = isInDraftMode ? editingComponents : (dashboard?.components || []);

    setPendingDimensionFilters((prev) => {
      // If filter is null, just remove it (we'll identify by component's dimension_id)
      if (!filter) {
        const component = componentsToSearch.find((c) => c.id === componentId);
        if (component?.filter_config?.dimension_id) {
          return prev.filter((f) => f.dimension_id !== component.filter_config!.dimension_id);
        }
        return prev;
      }

      // Add or update the filter
      // Remove any existing filter with the same dimension_id
      const withoutExisting = prev.filter((f) => f.dimension_id !== filter.dimension_id);
      return [...withoutExisting, filter];
    });
  }, [dashboard?.components, editingComponents, isInDraftMode]);

  // Handle layout change from ReactGridLayout (drag/resize)
  const handleLayoutChange = useCallback((layout: Layout) => {
    if (!isInDraftMode) return;

    let changed = false;
    setEditingComponents((draft) => {
      layout.forEach((item: LayoutItem) => {
        const component = draft.find((c) => c.id === item.i);
        if (component) {
          if (component.x !== item.x || component.y !== item.y || component.width !== item.w || component.height !== item.h) {
            changed = true;
            component.x = item.x;
            component.y = item.y;
            component.width = item.w;
            component.height = item.h;
          }
        }
      });
    });
    if (changed) {
      setHasUnsavedChanges(true);
    }
  }, [isInDraftMode, setEditingComponents]);

  // Handle opening the config sheet for a component
  const handleOpenConfig = useCallback((component: DashboardComponent) => {
    setConfiguringComponent(component);
    setConfigSheetOpen(true);
  }, []);

  // Handle saving component configuration
  const handleSaveComponentConfig = useCallback((updatedComponent: DashboardComponent) => {
    setEditingComponents((draft) => {
      const index = draft.findIndex((c) => c.id === updatedComponent.id);
      if (index !== -1) {
        draft[index] = updatedComponent;
      }
    });
    setHasUnsavedChanges(true);
  }, [setEditingComponents]);

  // Handle deleting a component
  const handleDeleteComponent = useCallback((componentId: string) => {
    setEditingComponents((draft) => {
      const index = draft.findIndex((c) => c.id === componentId);
      if (index !== -1) {
        draft.splice(index, 1);
      }
    });
    setHasUnsavedChanges(true);
  }, [setEditingComponents]);

  // Handle adding a new component
  const handleAddComponent = useCallback(() => {
    const newComponent: DashboardComponent = {
      id: `component-${Date.now()}`,
      title: '',
      x: 0,
      y: 0,
      width: 4,
      height: 6,
      object_type: 'ANALYSIS',
    };
    setEditingComponents((draft) => {
      draft.push(newComponent);
    });
    setHasUnsavedChanges(true);
    // Open config sheet for the new component
    setConfiguringComponent(newComponent);
    setConfigSheetOpen(true);
  }, [setEditingComponents]);

  // Core save function
  const executeSave = useCallback(async (components: DashboardComponent[], dateRangeConfig: any) => {
    if (!dashboardId) return;
    setIsSaving(true);
    try {
      await dashboardService.update(dashboardId, {
        components,
        date_range_config: dateRangeConfig,
      });
      queryClient.invalidateQueries({ queryKey: ['dashboard', dashboardId] });
      setHasUnsavedChanges(false);
      toastService.success('Dashboard saved successfully');
    } catch (err) {
      console.error('Failed to save dashboard:', err);
      toastService.error('Failed to save dashboard', {
        description: err instanceof Error ? err.message : 'An unexpected error occurred',
      });
    } finally {
      setIsSaving(false);
    }
  }, [dashboardId, queryClient]);

  // Rename saves immediately and only sends `name`, so unsaved layout edits stay pending
  const handleRenameDashboard = useCallback(async (newName: string) => {
    if (!dashboardId) return;
    try {
      await dashboardService.update(dashboardId, { name: newName });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['dashboard', dashboardId] }),
        queryClient.invalidateQueries({ queryKey: ['dashboards'] }),
      ]);
      toastService.success('Dashboard renamed');
    } catch (err) {
      console.error('Failed to rename dashboard:', err);
      toastService.error('Failed to rename dashboard', {
        description: err instanceof Error ? err.message : 'An unexpected error occurred',
      });
    }
  }, [dashboardId, queryClient]);

  // Save without dialog (used by unsaved changes guard)
  const handleSaveDashboard = useCallback(async () => {
    if (!dashboardId || !hasUnsavedChanges) return;
    await executeSave(editingComponents, dashboard?.date_range_config);
  }, [dashboardId, editingComponents, hasUnsavedChanges, executeSave, dashboard?.date_range_config]);

  // Save click handler — shows confirmation dialog if there are default value changes
  const handleSaveClick = useCallback(() => {
    if (hasDefaultValueChanges) {
      const initialChecked = new Map<string, boolean>();
      defaultValueChanges.forEach(change => initialChecked.set(change.id, true));
      setSaveChangeChecked(initialChecked);
      setShowSaveConfirmDialog(true);
    } else {
      handleSaveDashboard();
    }
  }, [hasDefaultValueChanges, defaultValueChanges, handleSaveDashboard]);

  // Confirmed save from dialog — applies checked default value changes
  const handleConfirmedSave = useCallback(async () => {
    setShowSaveConfirmDialog(false);

    // Clone editing components to apply filter default changes
    const componentsToSave = editingComponents.map(c => ({
      ...c,
      filter_config: c.filter_config ? { ...c.filter_config } : undefined,
    }));

    // Update filter defaults for checked filter changes
    defaultValueChanges.forEach(change => {
      if (change.type === 'filter_value' && saveChangeChecked.get(change.id)) {
        const dimId = change.id.replace('filter_', '');
        const component = componentsToSave.find(c =>
          c.object_type === 'FILTER' && c.filter_config?.dimension_id === dimId
        );
        if (component?.filter_config) {
          const pendingFilter = pendingDimensionFilters.find(f => f.dimension_id === dimId);
          component.filter_config.default_value = pendingFilter?.value
            ? (Array.isArray(pendingFilter.value) ? [...pendingFilter.value] : [pendingFilter.value])
            : undefined;
        }
      }
    });

    // Determine date range to save
    const dateRangeChange = defaultValueChanges.find(c => c.type === 'date_range');
    const saveDateRange = dateRangeChange && saveChangeChecked.get('date_range')
      ? pendingDateRangeConfig
      : dashboard?.date_range_config;

    // Update editing components state to reflect saved defaults
    setEditingComponents(componentsToSave);

    await executeSave(componentsToSave, saveDateRange);
  }, [defaultValueChanges, saveChangeChecked, editingComponents, pendingDimensionFilters, pendingDateRangeConfig, dashboard?.date_range_config, executeSave, setEditingComponents]);

  useUnsavedChangesGuard(hasUnsavedChanges, handleSaveDashboard);

  // Handle dashboard-wide refresh
  const handleRefresh = async (force: boolean = !supersetsEnabled) => {
    if (!dashboard) {
      return;
    }

    setIsRefreshing(true);
    setComponentErrors(new Map());
    try {
      // In draft mode use editingComponents (includes unsaved components), otherwise use saved dashboard components
      const componentsToRefresh = isInDraftMode ? editingComponents : (dashboard?.components || []);
      const analysisComponents = componentsToRefresh.filter(
        (c) => c.object_type === 'ANALYSIS' && c.object_id
      );

      if (analysisComponents.length === 0) {
        toastService.info('No chart components to refresh');
        setIsRefreshing(false);
        return;
      }

      // Fetch analyses by id directly — the paginated useAnalyses cache can miss
      // analyses that fall outside the first page.
      const analysisIds = Array.from(new Set(
        analysisComponents.map((c) => c.object_id!).filter((id): id is string => !!id)
      ));
      const fetchedAnalyses = await Promise.all(
        analysisIds.map((id) => analysisService.findById(id).catch(() => null))
      );
      const analysesById = new Map<string, Analysis>();
      fetchedAnalyses.forEach((a) => {
        if (a) analysesById.set(a.id, a);
      });
      const analysesToPrep = analysisComponents
        .map((c) => (c.object_id ? analysesById.get(c.object_id) : undefined))
        .filter((a): a is Analysis => a !== undefined);

      if (analysesToPrep.length === 0) {
        toastService.info('No valid analyses found');
        setIsRefreshing(false);
        return;
      }

      // Get skip date range analysis IDs
      const skipDateRangeAnalyses = analysisComponents
        .filter((c) => c.ignore_date_range && c.object_id)
        .map((c) => c.object_id!)
        .filter((id): id is string => id !== undefined);

      // Use the pending date range config (not saved to dashboard)
      const dateRangeConfig = pendingDateRangeConfig;

      // Use the pending dimension filters (not yet applied to charts)
      const filtersToUse = pendingDimensionFilters;

      // Call prep endpoint with dimension filters. The backend applies
      // DATASET_FRESHNESS_TOLERANCE_SECONDS as the default staleness window
      // when no explicit threshold is passed, so a fresh node is a no-op.
      const prepResult = await analysisService.prepAnalyses(
        analysesToPrep,
        filtersToUse, // Pass pending dimension filters
        dateRangeConfig,
        skipDateRangeAnalyses.length > 0 ? skipDateRangeAnalyses : undefined,
        force,
        undefined, // Let backend apply default freshness tolerance
        dashboardId || undefined // Build dashboard-level superset
      );

      // Capture the analysis→node mapping from the prep response
      const analysisNodeMap = prepResult.analysis_node_map || {};

      // Poll orchestration status until complete
      const finalOrchestration = await pollOrchestrationStatus(prepResult.record.id);

      // Use model_statuses + analysis_node_map to determine per-analysis errors
      const modelStatuses = finalOrchestration.model_statuses || {};

      // Process each analysis based on its model status
      let successCount = 0;
      let errorCount = 0;
      const errors: string[] = [];
      const newComponentErrors = new Map<string, string>();

      // Update active date range config and dimension filters BEFORE invalidating queries
      // This ensures charts reload with the new date range and filters without flicker
      setActiveDateRangeConfig(pendingDateRangeConfig);
      setDimensionFilters(pendingDimensionFilters);

      // Persist only at the moment of apply — pending-only changes never get saved
      persistAppliedFilters({
        dateRange: pendingDateRange,
        customStart: pendingCustomDateStart,
        customEnd: pendingCustomDateEnd,
        dimensionFilters: pendingDimensionFilters,
      });

      analysesToPrep.forEach((analysis) => {
        const nodeId = analysisNodeMap[analysis.id];
        const modelStatus = nodeId ? modelStatuses[nodeId] : undefined;

        if (modelStatus && modelStatus.status.toUpperCase() === 'ERROR') {
          errorCount++;
          const errorMsg = modelStatus.error || 'Build failed';
          errors.push(`${analysis.name || analysis.id}: ${errorMsg}`);

          // Find all components that reference this analysis and set their errors
          analysisComponents
            .filter((c) => c.object_id === analysis.id)
            .forEach((c) => {
              newComponentErrors.set(c.id, errorMsg);
            });

          // Still invalidate so the chart can attempt to show stale data or error
          queryClient.invalidateQueries({ queryKey: ['analysisData', analysis.id] });
        } else {
          // Success or no status info - invalidate queries to trigger data refresh
          queryClient.invalidateQueries({ queryKey: ['analysisData', analysis.id] });
          successCount++;
        }
      });

      setComponentErrors(newComponentErrors);

      // Show appropriate toast message
      if (errorCount === 0) {
        if (prepResult.build_skipped) {
          toastService.info('Existing dataset found. Skipping build');
        } else {
          toastService.success('Dashboard refreshed successfully');
        }
      } else if (successCount === 0) {
        toastService.error('All charts failed to refresh', {
          description: errors.join('; '),
        });
      } else {
        toastService.warning(`${successCount} chart(s) refreshed, ${errorCount} failed`, {
          description: errors.join('; '),
        });
      }
    } catch (err) {
      console.error('Failed to refresh dashboard:', err);
      toastService.error('Failed to refresh dashboard', {
        description: err instanceof Error ? err.message : 'An unexpected error occurred',
      });
    } finally {
      setIsRefreshing(false);
    }
  };

  if (!dashboardId) {
    return (
      <div className="p-8">
        <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md">
          <p className="text-destructive text-sm">No dashboard ID provided</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="w-full flex flex-col bg-page-bg overflow-hidden" style={{ height: 'calc(100vh)' }}>
        <div className="flex items-center justify-center h-full">
          <div className="flex flex-col items-center gap-4">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p className="text-sm text-muted-foreground">Loading dashboard...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !dashboard) {
    return (
      <div className="w-full flex flex-col bg-page-bg overflow-hidden" style={{ height: 'calc(100vh)' }}>
        <div className="p-8">
          <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md">
            <p className="text-destructive text-sm">
              {error ? 'Failed to load dashboard. Please try again.' : 'Dashboard not found.'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full flex flex-col bg-page-bg overflow-hidden" style={{ height: 'calc(100vh)' }}>
      {/* Top Bar */}
      <div className="flex flex-col md:flex-row md:items-center justify-between w-full px-4 md:px-6 py-3 md:py-4 page-header-surface flex-shrink-0 z-10 gap-3">
        <div className="flex items-start gap-2">
          <SidebarTrigger className="md:hidden h-5 w-5" />
          <div className="flex flex-col gap-1 md:gap-2 flex-1 min-w-0">
          {/* Breadcrumbs — hidden in embed (no /analytics access for embed users).
              On mobile, hide the current page name (title shows it). */}
          {!embeddedDashboardId && (
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbLink asChild>
                    <Link to="/analytics">Analytics</Link>
                  </BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator className="hidden md:block" />
                <BreadcrumbItem className="hidden md:block">
                  <BreadcrumbPage>{dashboard.name || 'Untitled Dashboard'}</BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
          )}
          {/* Page Title */}
          {isInDraftMode && !embeddedDashboardId ? (
            <EditableTitle
              title={dashboard.name || 'Untitled Dashboard'}
              onSave={handleRenameDashboard}
              entityLabel="Dashboard"
              titleClassName="text-lg md:text-xl"
            />
          ) : (
            <h1 className="text-lg md:text-xl font-semibold text-foreground">{dashboard.name || 'Untitled Dashboard'}</h1>
          )}
          {dashboard.description && (
            <p className="text-sm text-muted-foreground">{dashboard.description}</p>
          )}
        </div>
        </div>
        <div className="flex items-center gap-1.5 md:gap-2 flex-wrap [&>button]:text-xs [&>button]:md:text-sm [&>button]:h-8 [&>button]:md:h-9 [&>button]:px-2.5 [&>button]:md:px-4">
          {/* Edit Mode Controls */}
          {isInDraftMode && (
            <>
              <Button
                variant="outline"
                onClick={handleAddComponent}
                className="flex items-center gap-2"
              >
                <Plus className="h-4 w-4" />
                Add Component
              </Button>
              <Button
                onClick={handleSaveClick}
                disabled={!(hasUnsavedChanges || hasDefaultValueChanges) || isSaving}
                className="flex items-center gap-2"
              >
                {isSaving ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4" />
                    Save
                  </>
                )}
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowDeleteDialog(true)}
                className="text-destructive hover:text-destructive flex items-center gap-2"
              >
                <Trash2 className="h-4 w-4" />
                Delete
              </Button>
            </>
          )}
          {/* Date Range Selector */}
          <DateRangePicker
            value={pendingDateRange}
            customDateStart={pendingCustomDateStart}
            customDateEnd={pendingCustomDateEnd}
            onChange={handleDateRangeChange}
            disabled={isRefreshing}
            availableOptions={availableDateRangeOptions}
          />
          {dateRangeDiffersFromDefault && (
            <Button
              variant="ghost"
              size="icon"
              onClick={resetDateRangeToDefault}
              disabled={isRefreshing}
              title="Reset date range to default"
              aria-label="Reset date range to default"
              className="!h-8 !w-8 md:!h-9 md:!w-9 !px-0 md:!px-0 text-muted-foreground"
            >
              <RotateCcw className="h-4 w-4" />
            </Button>
          )}
          <div className="flex items-center">
            <Button
              onClick={() => handleRefresh()}
              disabled={isRefreshing || isChartRefreshing}
              className={`flex items-center gap-2 ${supersetsEnabled ? 'rounded-r-none' : ''}`}
            >
              {isRefreshing ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Refreshing...
                </>
              ) : (
                <>
                  <RefreshCw className="h-4 w-4" />
                  Refresh
                </>
              )}
            </Button>
            {supersetsEnabled && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    disabled={isRefreshing || isChartRefreshing}
                    className="rounded-l-none border-l border-primary-foreground/20 px-2"
                  >
                    <ChevronDown className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onClick={() => handleRefresh(false)}>
                    <RefreshCw className="h-4 w-4" />
                    Refresh
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => handleRefresh(true)}>
                    <RefreshCw className="h-4 w-4" />
                    Force Refresh
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden p-4 md:p-8 min-h-0">
        <div ref={containerRef} style={{ width: '100%' }}>
        {displayComponents.length === 0 ? (
          <div className="p-8 text-center">
            <p className="text-muted-foreground">This dashboard has no components yet.</p>
            {isInDraftMode && (
              <Button
                variant="outline"
                onClick={handleAddComponent}
                className="mt-4"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add your first component
              </Button>
            )}
          </div>
        ) : (
          containerMounted && isMobile ? (
            <div className="flex flex-col gap-4">
              {mobileComponents.map((component) => {
                const currentFilter = component.object_type === 'FILTER' && component.filter_config?.dimension_id
                  ? pendingDimensionFilters.find((f) => f.dimension_id === component.filter_config!.dimension_id) || null
                  : null;

                // On mobile, use a consistent height for charts rather than
                // the desktop grid height (which can be too tall or too short).
                const isChart = component.object_type !== 'FILTER' && component.object_type !== 'MARKDOWN';
                const heightPx = component.object_type === 'FILTER' ? 60
                  : component.object_type === 'MARKDOWN' ? Math.max(80, Math.min(200, component.height * 30))
                  : 360;  // charts get 360px on mobile

                return (
                  <div key={component.id} style={isChart ? { height: heightPx } : { minHeight: heightPx }}>
                    <DashboardComponentRenderer
                      component={component}
                      dateRangeConfig={activeDateRangeConfig}
                      dimensionFilters={dimensionFilters}
                      pendingDimensionFilters={pendingDimensionFilters}
                      forceShowPrepLoader={isRefreshing}
                      onFilterChange={handleFilterChange}
                      currentFilter={currentFilter}
                      disabled={isRefreshing}
                      onApplyFilters={handleRefresh}
                      filterApplyDisabled={isRefreshing || isChartRefreshing}
                      onChartRefresh={() => setIsChartRefreshing(true)}
                      onChartRefreshFinished={() => setIsChartRefreshing(false)}
                      chartRefreshDisabled={isChartRefreshing}
                      isEditing={false}
                      onConfigure={handleOpenConfig}
                      componentError={componentErrors.get(component.id)}
                    />
                  </div>
                );
              })}
            </div>
          ) : containerMounted ? (
            <ReactGridLayout
              className="layout"
              width={containerWidth}
              gridConfig={{ cols: 12, rowHeight: 30, margin: [30, 30] }}
              dragConfig={{ enabled: isInDraftMode, handle: '.drag', cancel: '.nodrag' }}
              resizeConfig={{ enabled: isInDraftMode }}
              onLayoutChange={handleLayoutChange}
            >
              {displayComponents.map((component) => {
                const currentFilter = component.object_type === 'FILTER' && component.filter_config?.dimension_id
                  ? pendingDimensionFilters.find((f) => f.dimension_id === component.filter_config!.dimension_id) || null
                  : null;

                return (
                  <div
                    key={component.id}
                    data-grid={{
                      x: component.x,
                      y: component.y,
                      w: component.width,
                      h: component.height,
                      minW: 2,
                      minH: 2,
                    }}
                    style={{ zIndex: component.object_type === 'FILTER' ? 1 : 0 }}
                  >
                    <DashboardComponentRenderer
                      component={component}
                      dateRangeConfig={activeDateRangeConfig}
                      dimensionFilters={dimensionFilters}
                      pendingDimensionFilters={pendingDimensionFilters}
                      forceShowPrepLoader={isRefreshing}
                      onFilterChange={handleFilterChange}
                      currentFilter={currentFilter}
                      disabled={isRefreshing}
                      onApplyFilters={handleRefresh}
                      filterApplyDisabled={isRefreshing || isChartRefreshing}
                      onChartRefresh={() => setIsChartRefreshing(true)}
                      onChartRefreshFinished={() => setIsChartRefreshing(false)}
                      chartRefreshDisabled={isChartRefreshing}
                      isEditing={isInDraftMode}
                      onConfigure={handleOpenConfig}
                      componentError={componentErrors.get(component.id)}
                    />
                  </div>
                );
              })}
            </ReactGridLayout>
          ) : null
        )}
        </div>

        {/* Component Config Sheet */}
        <ComponentConfigSheet
          component={configuringComponent}
          open={configSheetOpen}
          onOpenChange={setConfigSheetOpen}
          onSave={handleSaveComponentConfig}
          onDelete={handleDeleteComponent}
        />

        {/* Save Default Values Confirmation */}
        <AlertDialog open={showSaveConfirmDialog} onOpenChange={setShowSaveConfirmDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Update Default Values</AlertDialogTitle>
              <AlertDialogDescription>
                The following filter and date range values have changed from the saved defaults.
                Uncheck any you'd like to keep as-is.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <div className="py-4 space-y-3">
              {defaultValueChanges.map(change => (
                <div key={change.id} className="flex items-start gap-3">
                  <Checkbox
                    id={`save-change-${change.id}`}
                    checked={saveChangeChecked.get(change.id) ?? true}
                    onCheckedChange={(checked) => {
                      setSaveChangeChecked(prev => {
                        const next = new Map(prev);
                        next.set(change.id, !!checked);
                        return next;
                      });
                    }}
                  />
                  <Label htmlFor={`save-change-${change.id}`} className="flex flex-col gap-0.5 cursor-pointer">
                    <span className="text-sm font-medium">{change.label}</span>
                    <span className="text-xs text-muted-foreground">{change.description}</span>
                  </Label>
                </div>
              ))}
            </div>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={isSaving}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                disabled={isSaving}
                onClick={async (e) => {
                  e.preventDefault();
                  await handleConfirmedSave();
                }}
              >
                {isSaving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save'
                )}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Delete Dashboard Confirmation */}
        <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Delete Dashboard</AlertDialogTitle>
              <AlertDialogDescription>
                Are you sure you want to delete <strong>{dashboard.name || 'Untitled Dashboard'}</strong>? This action cannot be undone.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
              <AlertDialogAction
                disabled={isDeleting}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                onClick={async (e) => {
                  e.preventDefault();
                  await handleDeleteDashboard();
                }}
              >
                {isDeleting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  'Delete'
                )}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}

